import React, { useState } from "react";
import {
  View,
  Text,
  Image,
  Platform,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  useWindowDimensions,
} from "react-native";
import { SafeAreaInsetsContext } from "react-native-safe-area-context";
import { Mascot } from "./Mascot";
import GoogleButton from "./GoogleButton";
import { colors, fontFamily, radii } from "../theme";

// Shared scaffold for the Sign in / Sign up screens. Renders a polished
// two-column layout on wide viewports (web / tablet) — form on the left,
// an orange illustration panel with the mascot on the right — and collapses
// to a single centered column on phones. Both the mobile RN app and the
// RN-Web parent app render the same screens, so this one component covers
// both platforms (see WebApp.tsx routing Login/Signup to the RN screens).
//
// The art panel fakes the design's CSS gradient with a solid primary base
// plus a couple of translucent decorative blobs — no gradient dependency,
// renders identically on native and web.

const WIDE_BREAKPOINT = 900;

export type AuthMode = "signin" | "signup";

type Props = {
  mode: AuthMode;
  eyebrow: string;
  title: string;
  subtitle: string;
  onSwitchMode: (mode: AuthMode) => void;
  children: React.ReactNode;
  // Optional footer rendered under the form (e.g. "Back to welcome").
  footer?: React.ReactNode;
  // When provided, renders a "Continue with Google" button above the form.
  // The callback receives the Google ID token to exchange for app tokens.
  onGoogleToken?: (idToken: string) => void;
};

export default function AuthShell({
  mode,
  eyebrow,
  title,
  subtitle,
  onSwitchMode,
  children,
  footer,
  onGoogleToken,
}: Props) {
  const { width, height } = useWindowDimensions();
  const isWide = width >= WIDE_BREAKPOINT;
  // Device insets so the brand header clears the status bar / notch on iOS.
  // Read the context directly (not the useSafeAreaInsets hook) with a zero
  // fallback: the web build renders these same screens without a
  // SafeAreaProvider, where the hook would throw.
  const insets = React.useContext(SafeAreaInsetsContext) ?? {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  };
  // Whether the Google area should show the OR divider — true once the
  // Google button reports it actually rendered (web), or always on native.
  const [showGoogleDivider, setShowGoogleDivider] = useState(false);

  const formColumn = (
    <ScrollView
      style={styles.formScroll}
      contentContainerStyle={[
        styles.formScrollContent,
        isWide && styles.formScrollContentWide,
        // Clear the status bar / notch at the top (the base paddingTop:24 sits
        // under the iPhone status bar otherwise) and the home indicator below.
        {
          paddingTop: (isWide ? 28 : 24) + insets.top,
          paddingBottom: 32 + insets.bottom,
        },
      ]}
      showsVerticalScrollIndicator={false}
    >
      <View style={[styles.formWrap, isWide && styles.formWrapWide]}>
        {/* Brand — the new Kidly wordmark logo on web; native keeps the
            mascot-mark + text (the .webp asset isn't bundled for native). */}
        {Platform.OS === "web" ? (
          <Image
            source={{ uri: "/kidly-logo.webp" }}
            style={styles.brandLogo}
            resizeMode="contain"
          />
        ) : (
          <View style={styles.brand}>
            <View style={styles.brandMark}>
              <Mascot mood="happy" color="#fff" size={28} />
            </View>
            <Text style={styles.brandWord}>Kidly</Text>
          </View>
        )}

        <Text style={styles.eyebrow}>{eyebrow.toUpperCase()}</Text>
        <Text style={styles.h1}>{title}</Text>
        <Text style={styles.sub}>{subtitle}</Text>

        {/* Continue with Google — Google's official button on web (reliable
            popup), an email-fallback button on native. */}
        {onGoogleToken ? (
          <>
            <GoogleButton
              onToken={onGoogleToken}
              onAvailability={setShowGoogleDivider}
            />
            {showGoogleDivider ? (
              <View style={styles.orRow}>
                <View style={styles.orLine} />
                <Text style={styles.orText}>OR</Text>
                <View style={styles.orLine} />
              </View>
            ) : null}
          </>
        ) : null}

        {children}

        {footer ? <View style={styles.footer}>{footer}</View> : null}
      </View>
    </ScrollView>
  );

  return (
    <View style={[styles.root, { minHeight: height }]}>
      <View style={[styles.split, isWide && styles.splitWide]}>
        <View style={[styles.formCol, isWide && styles.formColWide]}>
          {formColumn}
        </View>

        {isWide && (
          <View style={styles.artCol}>
            {/* decorative translucent blobs for depth */}
            <View style={[styles.blob, styles.blobTop]} />
            <View style={[styles.blob, styles.blobBottom]} />

            <Text style={styles.quote}>
              Cartoons that teach.{"\n"}Made for ages 2–7.
            </Text>
            <Text style={styles.quoteTag}>
              Short, joyful videos paired with gentle questions — co-watched
              together, recapped on Sundays.
            </Text>

            <View style={styles.mascotStage}>
              <View style={styles.mascotDisc}>
                <View style={styles.mascotDiscInner}>
                  <Mascot mood="cheer" color="#fff" size={200} bob />
                </View>
              </View>
            </View>

            <View style={styles.trust}>
              <Text style={styles.trustItem}>✦  Kid-safe by default</Text>
              <Text style={styles.trustItem}>✦  Built with educators</Text>
              <Text style={styles.trustItem}>✦  Cancel anytime</Text>
            </View>
          </View>
        )}
      </View>
    </View>
  );
}

// ─── Shared form sub-components + styles, reused by Login/Signup ──────────

export function AuthField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <View style={authStyles.field}>
      <Text style={authStyles.fieldLabel}>{label.toUpperCase()}</Text>
      {children}
    </View>
  );
}

export const authStyles = StyleSheet.create({
  field: {
    marginBottom: 14,
  },
  fieldLabel: {
    fontFamily,
    fontSize: 11,
    fontWeight: "800",
    letterSpacing: 0.6,
    color: colors.ink3,
    marginBottom: 6,
  },
  input: {
    width: "100%",
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: colors.lineStrong,
    backgroundColor: colors.surface,
    borderRadius: radii.md,
    fontFamily,
    fontSize: 15,
    // Medium (not bold) so the placeholder text doesn't read as heavy.
    // RN/RNW apply the input's font weight to the placeholder too, and
    // there's no cross-platform ::placeholder hook.
    fontWeight: "500",
    color: colors.ink,
  },
  inputFocused: {
    borderColor: colors.primary,
  },
  row: {
    flexDirection: "row",
  },
  rowItem: {
    flex: 1,
  },
  submit: {
    width: "100%",
    marginTop: 8,
    paddingVertical: 16,
    borderRadius: radii.md,
    backgroundColor: colors.primary,
    alignItems: "center",
    borderBottomWidth: 3,
    borderBottomColor: "rgba(0,0,0,0.18)",
    shadowColor: colors.primary,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.3,
    shadowRadius: 16,
    elevation: 5,
  },
  submitDisabled: {
    opacity: 0.7,
  },
  submitText: {
    fontFamily,
    fontSize: 16,
    fontWeight: "900",
    color: "#fff",
    letterSpacing: 0.2,
  },
  // age chips
  chips: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  chip: {
    minWidth: 44,
    paddingVertical: 10,
    paddingHorizontal: 14,
    marginRight: 8,
    marginBottom: 8,
    borderRadius: radii.sm,
    borderWidth: 1,
    borderColor: colors.lineStrong,
    backgroundColor: colors.surface,
    alignItems: "center",
  },
  chipOn: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  chipText: {
    fontFamily,
    fontSize: 14,
    fontWeight: "800",
    color: colors.ink2,
  },
  chipTextOn: {
    color: "#fff",
  },
  // color swatches
  swatches: {
    flexDirection: "row",
  },
  swatch: {
    width: 36,
    height: 36,
    borderRadius: 18,
    marginRight: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  swatchOn: {
    borderWidth: 3,
    borderColor: colors.surface,
    // outer ring via shadow (RN can't do double border)
    shadowColor: colors.ink,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 1,
    shadowRadius: 2,
    elevation: 3,
  },
  // meta row (remember / forgot)
  meta: {
    marginTop: 14,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  metaText: {
    fontFamily,
    fontSize: 13,
    fontWeight: "700",
    color: colors.ink2,
  },
  link: {
    fontFamily,
    fontSize: 13,
    fontWeight: "800",
    color: colors.primaryInk,
  },
  tos: {
    marginTop: 16,
    fontFamily,
    fontSize: 12,
    fontWeight: "600",
    lineHeight: 18,
    color: colors.ink3,
  },
  error: {
    marginTop: 12,
    fontFamily,
    fontSize: 13,
    fontWeight: "700",
    color: "#D2502E",
  },
});

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  split: {
    flex: 1,
    flexDirection: "column",
  },
  splitWide: {
    flexDirection: "row",
  },
  // form column
  formCol: {
    flex: 1,
    backgroundColor: colors.surface,
  },
  formColWide: {
    flex: 1,
  },
  formScroll: {
    flex: 1,
  },
  formScrollContent: {
    flexGrow: 1,
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 32,
  },
  formScrollContentWide: {
    justifyContent: "center",
    paddingHorizontal: 56,
    paddingTop: 28,
  },
  formWrap: {
    width: "100%",
    alignSelf: "center",
  },
  formWrapWide: {
    maxWidth: 420,
  },
  brand: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 16,
  },
  brandLogo: {
    width: 150,
    height: 46,
    marginBottom: 16,
  },
  brandMark: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: colors.primary,
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    marginRight: 10,
  },
  brandWord: {
    fontFamily,
    fontSize: 22,
    fontWeight: "900",
    color: colors.ink,
    letterSpacing: -0.3,
  },
  eyebrow: {
    fontFamily,
    fontSize: 12,
    fontWeight: "800",
    letterSpacing: 1.4,
    color: colors.primaryInk,
    marginBottom: 10,
  },
  h1: {
    fontFamily,
    fontSize: 32,
    fontWeight: "900",
    letterSpacing: -0.5,
    lineHeight: 36,
    color: colors.ink,
    marginBottom: 10,
  },
  sub: {
    fontFamily,
    fontSize: 15,
    fontWeight: "600",
    lineHeight: 22,
    color: colors.ink2,
    marginBottom: 16,
    maxWidth: 380,
  },
  // Continue with Google
  googleBtn: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 14,
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: colors.lineStrong,
    backgroundColor: colors.surface,
    marginBottom: 18,
  },
  googleBtnText: {
    fontFamily,
    fontSize: 14,
    fontWeight: "800",
    color: colors.ink,
    marginLeft: 10,
  },
  orRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 18,
  },
  orLine: {
    flex: 1,
    height: 1,
    backgroundColor: colors.line,
  },
  orText: {
    fontFamily,
    fontSize: 11,
    fontWeight: "800",
    letterSpacing: 1.2,
    color: colors.ink3,
    marginHorizontal: 12,
  },
  toggle: {
    flexDirection: "row",
    alignSelf: "flex-start",
    padding: 4,
    backgroundColor: colors.bgWarm,
    borderRadius: radii.pill,
    marginBottom: 24,
  },
  toggleBtn: {
    paddingVertical: 8,
    paddingHorizontal: 22,
    borderRadius: radii.pill,
  },
  toggleBtnActive: {
    backgroundColor: colors.surface,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 2,
  },
  toggleText: {
    fontFamily,
    fontSize: 14,
    fontWeight: "800",
    color: colors.ink2,
  },
  toggleTextActive: {
    color: colors.ink,
  },
  footer: {
    marginTop: 22,
    alignItems: "flex-start",
  },
  // art column
  artCol: {
    flex: 1,
    backgroundColor: colors.primary,
    paddingHorizontal: 56,
    paddingTop: 56,
    paddingBottom: 40,
    overflow: "hidden",
  },
  blob: {
    position: "absolute",
    borderRadius: 9999,
    backgroundColor: "rgba(255,255,255,0.12)",
  },
  blobTop: {
    width: 420,
    height: 420,
    top: -120,
    right: -120,
  },
  blobBottom: {
    width: 320,
    height: 320,
    bottom: -100,
    left: -80,
    backgroundColor: "rgba(255,255,255,0.08)",
  },
  quote: {
    fontFamily,
    fontSize: 34,
    fontWeight: "900",
    lineHeight: 38,
    letterSpacing: -0.5,
    color: "#fff",
    maxWidth: 460,
    marginBottom: 14,
  },
  quoteTag: {
    fontFamily,
    fontSize: 15,
    fontWeight: "700",
    lineHeight: 22,
    color: "rgba(255,255,255,0.92)",
    maxWidth: 400,
  },
  mascotStage: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  mascotDisc: {
    width: 320,
    height: 320,
    borderRadius: 160,
    backgroundColor: "rgba(255,255,255,0.18)",
    alignItems: "center",
    justifyContent: "center",
  },
  mascotDiscInner: {
    marginTop: 36,
  },
  trust: {
    marginTop: "auto",
    paddingTop: 22,
    borderTopWidth: 1,
    borderTopColor: "rgba(255,255,255,0.25)",
    flexDirection: "row",
    flexWrap: "wrap",
  },
  trustItem: {
    fontFamily,
    fontSize: 13,
    fontWeight: "700",
    color: "rgba(255,255,255,0.92)",
    marginRight: 16,
    marginBottom: 4,
  },
});
